import {fileToBase64ObjectNoFileReader} from '@coolcolduk/util';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import {useCallback, useState} from 'react';
import {User} from '@cp/model';
import useSetAvatar from '../../../hooks/users/useSetAvatar';

export interface AccountInfoAvatarProps {
  user: User;
}

export default function AccountInfoAvatar({user}: AccountInfoAvatarProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [handleError, setHandleError] = useState<string | null>(null);
  const {setAvatar, loading, error: avatarError} = useSetAvatar();
  const avatarUrl = user?.photoUrl;

  const error = handleError || avatarError?.message || null;

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      setHandleError(null);

      const files = Array.from(e.dataTransfer.files);
      if (files.length === 0) return;

      const file = files[0];
      if (!file.type.startsWith('image/')) {
        setHandleError('Please select an image file');
        return;
      }

      try {
        const base64Object = await fileToBase64ObjectNoFileReader(file);
        await setAvatar(base64Object);
      } catch {
        // Error is handled by the hook's onError callback
      }
    },
    [setAvatar],
  );

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const {files} = e.target;
      if (!files || files.length === 0) return;

      const file = files[0];
      if (!file.type.startsWith('image/')) {
        setHandleError('Please select an image file');
        return;
      }

      setHandleError(null);
      try {
        const base64Object = await fileToBase64ObjectNoFileReader(file);
        await setAvatar(base64Object);
      } catch {
        // Error is handled by the hook's onError callback
      }
    },
    [setAvatar],
  );

  const handleRemoveAvatar = useCallback(async () => {
    setHandleError(null);
    try {
      await setAvatar(undefined);
    } catch {
      // Error is handled by the hook's onError callback
    }
  }, [setAvatar]);

  return (
    <Box sx={{mt: 3}}>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2}}>
        <Typography variant="subtitle1">Profile Picture</Typography>
        <IconButton
          size="small"
          color="error"
          onClick={handleRemoveAvatar}
          disabled={loading}
          sx={{
            '&:hover': {
              backgroundColor: 'error.light',
              color: 'white',
            },
          }}
        >
          <DeleteIcon sx={{fontSize: 18}} />
        </IconButton>
      </Box>

      <Box
        sx={{
          position: 'relative',
          width: 120,
          height: 120,
          borderRadius: '50%',
          overflow: 'hidden',
          border: '2px dashed',
          borderColor: isDragOver ? 'primary.main' : 'divider',
          backgroundColor: isDragOver ? 'action.hover' : 'background.paper',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => document.getElementById('avatar-upload')?.click()}
      >
        {/* Avatar Image */}
        <Avatar
          src={avatarUrl || undefined}
          alt={user.name || 'Profile picture'}
          sx={{
            width: '100%',
            height: '100%',
            fontSize: '3rem',
          }}
        >
          {user.name?.charAt(0) || user.email?.charAt(0) || 'U'}
        </Avatar>

        {/* Upload Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: isDragOver ? 1 : 0,
            transition: 'opacity 0.2s ease-in-out',
            '&:hover': {
              opacity: 1,
            },
          }}
        >
          <CloudUploadIcon sx={{color: 'white', fontSize: 32}} />
        </Box>

        {/* Loading Overlay */}
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="caption" color="white">
              Uploading...
            </Typography>
          </Box>
        )}
      </Box>

      {/* Hidden File Input */}
      <input id="avatar-upload" type="file" accept="image/*" onChange={handleFileSelect} style={{display: 'none'}} />

      {/* Error Message */}
      {error && (
        <Typography variant="body2" color="error" sx={{mt: 1}}>
          {error}
        </Typography>
      )}

      {/* Instructions */}
      <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
        Click or drag an image to upload a new profile picture
      </Typography>
    </Box>
  );
}
